// ============================================================
// flowField.ts — Hierarchical Flow Field Pathfinding
// ============================================================
//
// PRODUCTION PATHFINDING for GOOP ZONE.
//
// Architecture (3 layers):
//
//   Layer 1 — COST FIELD (built once per map generation)
//     A ~157×157 grid (64px cells) covering the 10,000×10,000 world.
//     Each cell stores a movement cost:
//       1   = open ground / blue zone (walkable)
//       5   = interior green zone (walkable but costly — enemies prefer blue)
//       255 = impassable (red walls, exterior green curves, out of bounds)
//
//   Layer 2 — INTEGRATION FIELD (rebuilt every ~0.6s when player moves 1+ cell)
//     BFS/Dijkstra flood from the player position outward.
//     Each cell stores its total cost-to-reach-player.
//     Impassable cells stay at Infinity.
//     LOCAL RADIUS CAP: only floods out to MAX_FLOOD_RADIUS cells from player
//     (enemies beyond this range keep stale data — they're off-screen anyway).
//
//   Layer 3 — FLOW FIELD (derived immediately after integration)
//     Each cell stores a normalized 2D direction vector pointing
//     toward the neighbor with the lowest integration cost.
//     Enemies look up their cell → get instant movement direction.
//     O(1) per enemy, scales to 500+ enemies with zero per-entity pathfinding.
//
// Bilinear interpolation smooths direction vectors between cells
// so enemies don't jerk at cell boundaries.
//
// PERFORMANCE NOTES (BETA 13.42+):
//   - CELL_SIZE bumped 32→64 (4× fewer cells: 97K → 24K)
//   - REBUILD_INTERVAL bumped 0.4→0.6s
//   - Local-radius BFS cap (MAX_FLOOD_RADIUS=60 cells = 3840px)
//   - Debounce: requires player to move ≥1 full cell before triggering rebuild
//   - Typed arrays reused between rebuilds (no GC pressure)
//
// ============================================================

import { WORLD_SIZE, ROOM_SIZE, GAP_WIDTH, GAP_OFFSET } from './roomMapgen';
import type { RoomMap, PlacedRoom } from './roomMapgen';
import {
  classifyPoint,
  pointInPolygon,
  type ExteriorGreenCurve,
} from './roomCollisionGrid';

// ─── Grid Constants ─────────────────────────────────────────────
export const CELL_SIZE = 64;
export const GRID_W = Math.ceil(WORLD_SIZE / CELL_SIZE); // ~157
export const GRID_H = Math.ceil(WORLD_SIZE / CELL_SIZE); // ~157
export const GRID_TOTAL = GRID_W * GRID_H;

// ─── Cost Constants ─────────────────────────────────────────────
const COST_OPEN      = 1;    // Open ground, blue zones
const COST_GREEN     = 5;    // Interior green corners — passable but discouraged
const COST_IMPASS    = 255;  // Walls, exterior greens, out of bounds

// ─── Performance Tuning ─────────────────────────────────────────
const REBUILD_INTERVAL = 0.6;       // seconds between flow field rebuilds
const MAX_FLOOD_RADIUS = 60;        // cells from player — limits BFS scope (~3840px)
const MAX_FLOOD_COST   = MAX_FLOOD_RADIUS * COST_GREEN * 1.41; // max integration distance

// ─── Flow Field State ───────────────────────────────────────────
/** Cost field — built once per map. Index = y * GRID_W + x */
let costField: Uint8Array | null = null;

/** Integration field — distance-to-player for each cell (REUSED between rebuilds) */
let integrationField: Float32Array | null = null;

/** Flow vectors — packed as two Int8Arrays for X and Y components (REUSED) */
let flowX: Int8Array | null = null;
let flowY: Int8Array | null = null;

/** Last player cell position (for dirty check) */
let lastPlayerCellX = -1;
let lastPlayerCellY = -1;

/** Cached map seed to detect map changes */
let cachedSeed = -1;

/** Timer for rebuild throttling */
let rebuildTimer = 0;

/** Pre-allocated BFS queue (reused between rebuilds to avoid GC) */
let bfsQueue: Int32Array | null = null;

// ─── BETA 13.79 — Pathfinding handoff Fix 1 + Fix 2 ──────────────────────────
// Trap-cell penalty: bot calls markCellAsTrap(x, y) when it logs `attempt > 4`
// at the same coord. The integration BFS then multiplies cellCost by
// (1 + trapPenalty / 100), making the bot route AROUND known-bad cells.
// Cleared on every new map (resetFlowField).
const _trapCells: Map<string, number> = new Map();

// Wall-adjacency penalty (Fix 2): precomputed once per cost-field build.
// Any open cell with a COST_IMPASS neighbor gets a +30 multiplicative penalty,
// which pushes flow paths off the wall-skim line where the bot was getting
// trapped at coords like (2960, 4631) and (9100, 2620).
let _wallAdjPenalty: Uint8Array | null = null;
const WALL_ADJ_PENALTY = 30;

// ─── 8-directional neighbor offsets ─────────────────────────────
const DIR_DX = [-1, 0, 1, -1, 1, -1, 0, 1];
const DIR_DY = [-1, -1, -1, 0, 0, 1, 1, 1];
const DIR_COST = [1.41, 1, 1.41, 1, 1, 1.41, 1, 1.41]; // diagonal = √2

// ============================================================
//  LAYER 1: COST FIELD
// ============================================================

/**
 * Build the cost field from room data. Called once per map generation.
 * Samples each 64px cell center against room collision zones and
 * exterior green curve polygons.
 */
export function buildCostField(roomMap: RoomMap): void {
  if (cachedSeed === roomMap.seed && costField) return;
  cachedSeed = roomMap.seed;

  const field = new Uint8Array(GRID_TOTAL);
  // Default: all cells are open ground
  field.fill(COST_OPEN);

  const rooms = roomMap.rooms;
  const exteriorCurves = roomMap.exteriorGreenCurves ?? [];

  // ─── BETA 15.17 — Multi-sample cell classification ───────────────────────────
  // Root-cause fix for the week-long "enemies/bots stuck on walls in same spots"
  // bug. The 64px nav cells were classified by a single CENTER point. Corner clip
  // triangles (48px) and exterior bezier curves can overlap a cell while missing
  // its center, so the cell was marked walkable but entities physically hit the
  // wall and got stuck. Now we sample 5 points per cell (center + 4 inset corners)
  // and use the worst zone — any 'red' sample → cell IMPASS.
  // Effective walkable area shrinks ~32px against every wall edge (a buffer that
  // prevents wall-grazing). Doorway-widen pass below also multi-samples so 192px
  // doors stay 3 cells wide. Same rule applied to exterior green curves.
  const SAMPLE_INSET = 4;
  const SAMPLE_OFF = CELL_SIZE / 2 - SAMPLE_INSET; // 28
  const SAMPLE_OFFSETS: ReadonlyArray<readonly [number, number]> = [
    [0, 0],
    [-SAMPLE_OFF, -SAMPLE_OFF],
    [ SAMPLE_OFF, -SAMPLE_OFF],
    [-SAMPLE_OFF,  SAMPLE_OFF],
    [ SAMPLE_OFF,  SAMPLE_OFF],
  ];

  // ── Mark room interiors ──
  for (const room of rooms) {
    const roomCellX0 = Math.floor(room.x / CELL_SIZE);
    const roomCellY0 = Math.floor(room.y / CELL_SIZE);
    const roomCellX1 = Math.ceil((room.x + room.size) / CELL_SIZE);
    const roomCellY1 = Math.ceil((room.y + room.size) / CELL_SIZE);

    for (let cy = roomCellY0; cy < roomCellY1; cy++) {
      for (let cx = roomCellX0; cx < roomCellX1; cx++) {
        if (cx < 0 || cx >= GRID_W || cy < 0 || cy >= GRID_H) continue;

        const wxc = cx * CELL_SIZE + CELL_SIZE / 2;
        const wyc = cy * CELL_SIZE + CELL_SIZE / 2;

        // Multi-sample: 5 points (center + 4 inset corners). Worst zone wins.
        let worstZone: 'red' | 'green' | 'blue' = 'blue';
        let anySampleInRoom = false;
        for (let s = 0; s < SAMPLE_OFFSETS.length; s++) {
          const wx = wxc + SAMPLE_OFFSETS[s][0];
          const wy = wyc + SAMPLE_OFFSETS[s][1];
          const lx = wx - room.x;
          const ly = wy - room.y;
          if (lx >= 0 && lx < room.size && ly >= 0 && ly < room.size) {
            anySampleInRoom = true;
            const z = classifyPoint(lx, ly);
            if (z === 'red') { worstZone = 'red'; break; }
            if (z === 'green' && worstZone === 'blue') worstZone = 'green';
          }
        }

        if (anySampleInRoom) {
          const idx = cy * GRID_W + cx;
          if (worstZone === 'red') {
            field[idx] = COST_IMPASS;
          } else if (worstZone === 'green') {
            field[idx] = COST_GREEN;
          }
          // blue stays at COST_OPEN (1)
        }
      }
    }
  }

  // ── Mark exterior green curves as impassable ──
  // BETA 15.17 — multi-sample to catch curves that overlap a cell but miss center.
  for (const curve of exteriorCurves) {
    if (!curve.polygon || curve.polygon.length < 3) continue;

    // Compute bounding box of polygon
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const pt of curve.polygon) {
      if (pt.x < minX) minX = pt.x;
      if (pt.y < minY) minY = pt.y;
      if (pt.x > maxX) maxX = pt.x;
      if (pt.y > maxY) maxY = pt.y;
    }

    const cx0 = Math.max(0, Math.floor(minX / CELL_SIZE));
    const cy0 = Math.max(0, Math.floor(minY / CELL_SIZE));
    const cx1 = Math.min(GRID_W - 1, Math.ceil(maxX / CELL_SIZE));
    const cy1 = Math.min(GRID_H - 1, Math.ceil(maxY / CELL_SIZE));

    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const wxc = cx * CELL_SIZE + CELL_SIZE / 2;
        const wyc = cy * CELL_SIZE + CELL_SIZE / 2;

        // Any of the 5 samples inside polygon → mark cell IMPASS
        let hit = false;
        for (let s = 0; s < SAMPLE_OFFSETS.length; s++) {
          const wx = wxc + SAMPLE_OFFSETS[s][0];
          const wy = wyc + SAMPLE_OFFSETS[s][1];
          if (pointInPolygon(wx, wy, curve.polygon)) { hit = true; break; }
        }
        if (hit) field[cy * GRID_W + cx] = COST_IMPASS;
      }
    }
  }

  // ── Mark world border as impassable ──
  for (let cy = 0; cy < GRID_H; cy++) {
    for (let cx = 0; cx < GRID_W; cx++) {
      const wx = cx * CELL_SIZE + CELL_SIZE / 2;
      const wy = cy * CELL_SIZE + CELL_SIZE / 2;
      if (wx < 0 || wx >= WORLD_SIZE || wy < 0 || wy >= WORLD_SIZE) {
        field[cy * GRID_W + cx] = COST_IMPASS;
      }
    }
  }

  // ── Widen doorway corridors ──
  // BETA 15.17 — must mirror the multi-sample logic above. Without this, the
  // multi-sample fix would shrink doorways from 3 cells to 1 cell wide because
  // outer doorway cells have wall in their corners and would stay IMPASS.
  // Now: a cell is restored to OPEN if ANY of its 5 sample points is inside
  // the gap rectangle.
  for (const room of rooms) {
    for (const gap of room.gaps) {
      const gapCenterX = gap.x + gap.w / 2;
      const gapCenterY = gap.y + gap.h / 2;

      const halfWidth = Math.ceil(gap.w / CELL_SIZE / 2) + 1;
      const halfHeight = Math.ceil(gap.h / CELL_SIZE / 2) + 1;

      const gcx = Math.floor(gapCenterX / CELL_SIZE);
      const gcy = Math.floor(gapCenterY / CELL_SIZE);

      for (let dy = -halfHeight; dy <= halfHeight; dy++) {
        for (let dx = -halfWidth; dx <= halfWidth; dx++) {
          const cx = gcx + dx;
          const cy = gcy + dy;
          if (cx >= 0 && cx < GRID_W && cy >= 0 && cy < GRID_H) {
            const idx = cy * GRID_W + cx;
            if (field[idx] === COST_IMPASS) {
              const wxc = cx * CELL_SIZE + CELL_SIZE / 2;
              const wyc = cy * CELL_SIZE + CELL_SIZE / 2;
              // Multi-sample: any of 5 points inside gap rect → restore cell.
              let inGap = false;
              for (let s = 0; s < SAMPLE_OFFSETS.length; s++) {
                const wx = wxc + SAMPLE_OFFSETS[s][0];
                const wy = wyc + SAMPLE_OFFSETS[s][1];
                if (wx >= gap.x && wx <= gap.x + gap.w &&
                    wy >= gap.y && wy <= gap.y + gap.h) {
                  inGap = true;
                  break;
                }
              }
              if (inGap) field[idx] = COST_OPEN;
            }
          }
        }
      }
    }
  }

  costField = field;

  // BETA 13.79 — precompute wall-adjacency penalty (Fix 2). Any open cell with
  // a COST_IMPASS neighbor gets a +30 multiplicative penalty in the BFS.
  // Pushes paths off the wall-skim line that was trapping the bot at (2960, 4631).
  _wallAdjPenalty = new Uint8Array(GRID_TOTAL);
  for (let cy = 0; cy < GRID_H; cy++) {
    for (let cx = 0; cx < GRID_W; cx++) {
      const i = cy * GRID_W + cx;
      if (field[i] === COST_IMPASS) continue;
      // Check 8 neighbors for any wall.
      let adjWall = false;
      for (let d = 0; d < 8 && !adjWall; d++) {
        const nx = cx + DIR_DX[d];
        const ny = cy + DIR_DY[d];
        if (nx < 0 || nx >= GRID_W || ny < 0 || ny >= GRID_H) continue;
        if (field[ny * GRID_W + nx] === COST_IMPASS) adjWall = true;
      }
      if (adjWall) _wallAdjPenalty[i] = WALL_ADJ_PENALTY;
    }
  }

  // Trap penalties carry over within the same map (same seed). On a fresh map
  // resetFlowField() will clear them. We do NOT clear here — same-seed rebuilds
  // (e.g. the lazy 0.6s rebuild) should preserve the bot's learned trap list.

  // Allocate persistent typed arrays for integration + flow fields
  integrationField = new Float32Array(GRID_TOTAL);
  flowX = new Int8Array(GRID_TOTAL);
  flowY = new Int8Array(GRID_TOTAL);

  // Pre-allocate BFS queue (worst case = all cells, but typically much less)
  bfsQueue = new Int32Array(GRID_TOTAL);

  lastPlayerCellX = -1;
  lastPlayerCellY = -1;
}

// ============================================================
//  LAYER 2: INTEGRATION FIELD (Dijkstra flood from player)
// ============================================================

/**
 * Rebuild the integration field using Dijkstra's algorithm from the player position.
 * LOCAL RADIUS CAP: stops flooding beyond MAX_FLOOD_RADIUS cells from player.
 * REUSES pre-allocated typed arrays to avoid GC pressure.
 */
function rebuildIntegrationField(playerCellX: number, playerCellY: number): void {
  if (!costField || !integrationField || !bfsQueue) return;

  const dist = integrationField;
  dist.fill(Infinity);

  const queue = bfsQueue;
  let head = 0;
  let tail = 0;

  // Seed: player cell
  const startIdx = playerCellY * GRID_W + playerCellX;
  if (costField[startIdx] === COST_IMPASS) {
    // Player is in a wall cell — find nearest open cell
    let bestIdx = startIdx;
    let bestDist = Infinity;
    for (let dy = -3; dy <= 3; dy++) {
      for (let dx = -3; dx <= 3; dx++) {
        const nx = playerCellX + dx;
        const ny = playerCellY + dy;
        if (nx >= 0 && nx < GRID_W && ny >= 0 && ny < GRID_H) {
          const ni = ny * GRID_W + nx;
          if (costField[ni] !== COST_IMPASS) {
            const d = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; bestIdx = ni; }
          }
        }
      }
    }
    dist[bestIdx] = 0;
    queue[tail++] = bestIdx;
  } else {
    dist[startIdx] = 0;
    queue[tail++] = startIdx;
  }

  // BFS/Dijkstra flood with LOCAL RADIUS CAP
  while (head < tail) {
    const ci = queue[head++];
    const cx = ci % GRID_W;
    const cy = (ci - cx) / GRID_W;
    const cd = dist[ci];

    // LOCAL RADIUS CAP: stop expanding if we've gone too far
    if (cd > MAX_FLOOD_COST) continue;

    for (let d = 0; d < 8; d++) {
      const nx = cx + DIR_DX[d];
      const ny = cy + DIR_DY[d];
      if (nx < 0 || nx >= GRID_W || ny < 0 || ny >= GRID_H) continue;

      const ni = ny * GRID_W + nx;
      const cellCost = costField[ni];
      if (cellCost === COST_IMPASS) continue;

      // For diagonal movement, check that both cardinal neighbors are passable
      // (prevents cutting through wall corners)
      if (d === 0 || d === 2 || d === 5 || d === 7) {
        const n1 = costField[cy * GRID_W + nx]; // horizontal neighbor
        const n2 = costField[ny * GRID_W + cx]; // vertical neighbor
        if (n1 === COST_IMPASS || n2 === COST_IMPASS) continue;
      }

      // BETA 13.79 — apply wall-adjacency + trap-cell penalties multiplicatively
      // on top of the base cellCost. Cell stays passable (still > 0 < IMPASS) but
      // routes around known-bad terrain.
      let cost = cellCost;
      if (_wallAdjPenalty && _wallAdjPenalty[ni] > 0) cost *= 1 + _wallAdjPenalty[ni] / 100;
      if (_trapCells.size > 0) {
        const trap = _trapCells.get(`${nx},${ny}`);
        if (trap) cost *= 1 + trap / 100;
      }
      const nd = cd + DIR_COST[d] * cost;
      if (nd < dist[ni]) {
        dist[ni] = nd;
        queue[tail++] = ni;
      }
    }
  }

  // integrationField is already updated in-place (dist === integrationField)
}

// ============================================================
//  LAYER 3: FLOW FIELD (direction vectors)
// ============================================================

/**
 * Derive flow vectors from the integration field.
 * Each cell points toward its neighbor with the lowest integration cost.
 * REUSES pre-allocated typed arrays.
 */
function rebuildFlowField(): void {
  if (!integrationField || !costField || !flowX || !flowY) return;

  const fx = flowX;
  const fy = flowY;

  for (let cy = 0; cy < GRID_H; cy++) {
    for (let cx = 0; cx < GRID_W; cx++) {
      const ci = cy * GRID_W + cx;

      // Impassable cells or unreached cells get zero vector
      if (costField[ci] === COST_IMPASS || integrationField[ci] === Infinity) {
        fx[ci] = 0;
        fy[ci] = 0;
        continue;
      }

      let bestDist = integrationField[ci];
      let bestDx = 0;
      let bestDy = 0;

      for (let d = 0; d < 8; d++) {
        const nx = cx + DIR_DX[d];
        const ny = cy + DIR_DY[d];
        if (nx < 0 || nx >= GRID_W || ny < 0 || ny >= GRID_H) continue;

        const ni = ny * GRID_W + nx;
        if (costField[ni] === COST_IMPASS) continue;

        // Diagonal corner-cutting check
        if (d === 0 || d === 2 || d === 5 || d === 7) {
          const n1 = costField[cy * GRID_W + nx];
          const n2 = costField[ny * GRID_W + cx];
          if (n1 === COST_IMPASS || n2 === COST_IMPASS) continue;
        }

        const nd = integrationField[ni];
        if (nd < bestDist) {
          bestDist = nd;
          bestDx = DIR_DX[d];
          bestDy = DIR_DY[d];
        }
      }

      fx[ci] = bestDx;
      fy[ci] = bestDy;
    }
  }

  // flowX and flowY are already updated in-place
}

// ============================================================
//  PUBLIC API
// ============================================================

/**
 * Initialize the flow field system for a new map.
 * Call once after map generation.
 */
export function initFlowField(roomMap: RoomMap): void {
  buildCostField(roomMap);
}

/**
 * Update the flow field. Call every frame with dt.
 * Only rebuilds when:
 *   1. The rebuild timer has expired (REBUILD_INTERVAL = 0.6s)
 *   2. The player has moved to a DIFFERENT cell than last rebuild
 * This debounces edge-of-cell oscillation and limits CPU cost.
 */
// BETA 13.70 FPS: async rebuild via requestIdleCallback. The BFS still costs 2-4ms;
// running it on the idle callback instead of mid-frame eliminates the periodic stutter
// (the "flicker every 0.6s" effect the user described). Enemies use the last-known
// field for 1-2 frames while the new one is being computed — invisible in practice.
let _pendingRebuild = false;
type IdleCb = (cb: () => void, opts?: { timeout: number }) => number;
const _ric: IdleCb | null =
  typeof globalThis !== 'undefined' && typeof (globalThis as { requestIdleCallback?: IdleCb }).requestIdleCallback === 'function'
    ? (globalThis as { requestIdleCallback: IdleCb }).requestIdleCallback
    : null;

export function updateFlowField(
  dt: number,
  playerX: number,
  playerY: number,
): void {
  if (!costField) return;

  rebuildTimer -= dt;
  if (rebuildTimer > 0) return; // Timer hasn't expired yet — skip entirely

  const pcx = Math.floor(playerX / CELL_SIZE);
  const pcy = Math.floor(playerY / CELL_SIZE);

  // Only rebuild if player moved to a different cell than last rebuild
  if (pcx === lastPlayerCellX && pcy === lastPlayerCellY) return;
  // Skip enqueueing if a rebuild is already pending (avoids stacking BFS work).
  if (_pendingRebuild) return;

  // Player moved to a new cell and timer expired — schedule async rebuild
  lastPlayerCellX = pcx;
  lastPlayerCellY = pcy;
  rebuildTimer = REBUILD_INTERVAL;
  _pendingRebuild = true;

  const doRebuild = () => {
    rebuildIntegrationField(pcx, pcy);
    rebuildFlowField();
    _pendingRebuild = false;
  };

  if (_ric) {
    _ric(doRebuild, { timeout: 200 }); // hard cap so the field never goes stale
  } else {
    // Safari fallback: defer to next task. Still off the current frame's critical path.
    setTimeout(doRebuild, 0);
  }
}

/**
 * Force an immediate rebuild (e.g., on map load or teleport).
 */
export function forceRebuild(playerX: number, playerY: number): void {
  if (!costField) return;
  lastPlayerCellX = Math.floor(playerX / CELL_SIZE);
  lastPlayerCellY = Math.floor(playerY / CELL_SIZE);
  rebuildTimer = REBUILD_INTERVAL;
  rebuildIntegrationField(lastPlayerCellX, lastPlayerCellY);
  rebuildFlowField();
}

/**
 * Get the flow direction at a world position.
 * Returns a normalized {x, y} direction vector.
 * Uses bilinear interpolation for smooth movement between cells.
 */
// BETA 13.90 FPS — Hot path called 30-50× per frame from steerTowardPlayer().
// Performance recording showed this was 80%+ of frame budget under the minified
// name `Sw`. The previous implementation allocated 5 objects per call (4 from
// the inner `sample` arrow + 1 for the return value). At 50 enemies × 60fps
// = 15,000 allocations/sec just for steering, causing constant GC pressure.
//
// Rewritten to:
// - Inline the 4 corner samples (no inner arrow function, no per-corner objects)
// - Return into a shared output buffer (no per-call return allocation)
// - Manually clamp instead of Math.max/Math.min (saves 8 function calls/call)
//
// Callers must not retain the returned object — read x/y immediately.
const _flowDirOut = { x: 0, y: 0 };
export function getFlowDirection(
  worldX: number,
  worldY: number,
): { x: number; y: number } | null {
  if (!flowX || !flowY || !integrationField) return null;

  // Convert to cell-space (continuous)
  const fcx = worldX / CELL_SIZE - 0.5;
  const fcy = worldY / CELL_SIZE - 0.5;

  const cx0 = Math.floor(fcx);
  const cy0 = Math.floor(fcy);
  const cx1 = cx0 + 1;
  const cy1 = cy0 + 1;

  // Interpolation weights
  const tx = fcx - cx0;
  const ty = fcy - cy0;

  // Manually clamp each corner (replaces Math.max/Math.min — 4× fewer calls)
  const W = GRID_W;
  const H = GRID_H;
  const cx0c = cx0 < 0 ? 0 : cx0 >= W ? W - 1 : cx0;
  const cx1c = cx1 < 0 ? 0 : cx1 >= W ? W - 1 : cx1;
  const cy0c = cy0 < 0 ? 0 : cy0 >= H ? H - 1 : cy0;
  const cy1c = cy1 < 0 ? 0 : cy1 >= H ? H - 1 : cy1;

  // Inline the 4 corner samples — direct typed-array access, no per-corner object
  const i00 = cy0c * W + cx0c;
  const i10 = cy0c * W + cx1c;
  const i01 = cy1c * W + cx0c;
  const i11 = cy1c * W + cx1c;

  const dx00 = flowX[i00], dy00 = flowY[i00];
  const dx10 = flowX[i10], dy10 = flowY[i10];
  const dx01 = flowX[i01], dy01 = flowY[i01];
  const dx11 = flowX[i11], dy11 = flowY[i11];

  // Bilinear interpolation weights (compute once, reuse)
  const w00 = (1 - tx) * (1 - ty);
  const w10 = tx * (1 - ty);
  const w01 = (1 - tx) * ty;
  const w11 = tx * ty;

  const dx = dx00 * w00 + dx10 * w10 + dx01 * w01 + dx11 * w11;
  const dy = dy00 * w00 + dy10 * w10 + dy01 * w01 + dy11 * w11;

  // Normalize. (squared-len check first to avoid sqrt when stuck)
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 0.0001) return null;
  const invLen = 1 / Math.sqrt(lenSq);

  // Reuse the static output object — caller must not retain
  _flowDirOut.x = dx * invLen;
  _flowDirOut.y = dy * invLen;
  return _flowDirOut;
}

/**
 * Get the raw integration distance at a world position.
 * Useful for AI decisions (e.g., "am I getting closer to the player?")
 */
export function getDistanceToPlayer(worldX: number, worldY: number): number {
  if (!integrationField) return Infinity;
  const cx = Math.floor(worldX / CELL_SIZE);
  const cy = Math.floor(worldY / CELL_SIZE);
  if (cx < 0 || cx >= GRID_W || cy < 0 || cy >= GRID_H) return Infinity;
  return integrationField[cy * GRID_W + cx];
}

/**
 * Check if a world position is on a walkable cell in the cost field.
 */
export function isFlowCellWalkable(worldX: number, worldY: number): boolean {
  if (!costField) return true;
  const cx = Math.floor(worldX / CELL_SIZE);
  const cy = Math.floor(worldY / CELL_SIZE);
  if (cx < 0 || cx >= GRID_W || cy < 0 || cy >= GRID_H) return false;
  return costField[cy * GRID_W + cx] !== COST_IMPASS;
}

/**
 * Check if the flow field is initialized and ready.
 */
export function isFlowFieldReady(): boolean {
  return costField !== null && flowX !== null && flowY !== null;
}

/**
 * Get the cost field for debug rendering.
 */
export function getCostField(): Uint8Array | null {
  return costField;
}

/**
 * Get the integration field for debug rendering.
 */
export function getIntegrationField(): Float32Array | null {
  return integrationField;
}

/**
 * Get flow vectors for debug rendering.
 */
export function getFlowVectors(): { x: Int8Array; y: Int8Array } | null {
  if (!flowX || !flowY) return null;
  return { x: flowX, y: flowY };
}

/**
 * Reset flow field state (e.g., on game restart).
 */
export function resetFlowField(): void {
  costField = null;
  integrationField = null;
  flowX = null;
  flowY = null;
  bfsQueue = null;
  lastPlayerCellX = -1;
  lastPlayerCellY = -1;
  cachedSeed = -1;
  rebuildTimer = 0;
  // BETA 13.79 — clear pathfinding learned-state on a new map.
  _trapCells.clear();
  _wallAdjPenalty = null;
}

// ─── BETA 13.79 — Pathfinding handoff Fix 1 (trap-cell blacklist) ───────────
/**
 * Mark a world coordinate as a "trap" — bot's stuck-detection calls this when
 * it logs `attempt > 4` at the same cell (per the pathfinding handoff). Future
 * integration BFS rebuilds multiply the cell's traversal cost by (1 + penalty/100),
 * so subsequent paths route around it instead of grinding the same wall corner.
 *
 * Penalty stacks (each call adds +100, capped at 1000) so persistently-stuck
 * cells become near-impassable. Trap state is cleared on resetFlowField().
 */
export function markCellAsTrap(worldX: number, worldY: number): void {
  const cx = Math.floor(worldX / CELL_SIZE);
  const cy = Math.floor(worldY / CELL_SIZE);
  if (cx < 0 || cx >= GRID_W || cy < 0 || cy >= GRID_H) return;
  const key = `${cx},${cy}`;
  const cur = _trapCells.get(key) ?? 0;
  _trapCells.set(key, Math.min(1000, cur + 100));
  // Force the next updateFlowField call to actually rebuild — the cached cell
  // won't move otherwise (debounced rebuild waits for player to change cells).
  rebuildTimer = 0;
  lastPlayerCellX = -1;
  lastPlayerCellY = -1;
}

/** For dev-panel inspection of which cells the bot has flagged as traps. */
export function getTrapCells(): ReadonlyMap<string, number> {
  return _trapCells;
}

/** Manual clear (e.g. after a map regen mid-run). */
export function clearTrapCells(): void {
  _trapCells.clear();
  rebuildTimer = 0;
}
